import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, Shield, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { requestPasswordReset, resetPassword } from '@/services/api/auth';
import { useApiResponse } from '@/hooks/useApiResponse';

const ChangePassword = () => {
  const [username, setUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpOrToken, setOtpOrToken] = useState('');
  const [mode, setMode] = useState<'change' | 'forgot'>('forgot');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0); // resend OTP cooldown in seconds
  const [otpRequested, setOtpRequested] = useState(false);
  const navigate = useNavigate();
  const { data: unwrapData } = useApiResponse();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation based on mode
    if (!username) {
      toast.error('Username is required');
      return;
    }

    if (mode === 'change') {
      if (!currentPassword || !newPassword || !confirmPassword) {
        toast.error('Please fill in all fields');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }
      if (newPassword.length < 8) {
        toast.error('New password must be at least 8 characters long');
        return;
      }
      if (currentPassword === newPassword) {
        toast.error('New password must be different from current password');
        return;
      }
    } else {
      // forgot mode: need new + confirm (+ optional token/otp depending on backend)
      if (!newPassword || !confirmPassword) {
        toast.error('Please enter the new password and confirm it');
        return;
      }
      if (newPassword !== confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }
      if (newPassword.length < 8) {
        toast.error('New password must be at least 8 characters long');
        return;
      }
      // If user entered an OTP, validate format (6 numeric digits)
      if (otpOrToken && !/^\d{6}$/.test(otpOrToken.trim())) {
        toast.error('OTP must be 6 numeric digits');
        return;
      }
    }

    setLoading(true);

    try {
      if (mode === 'change') {
        // Existing change via logged-in verification still allowed
        const resp = await fetch('/api/v1/auth/change-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: username, currentPassword, newPassword })
        });
        if (resp.ok) {
          toast.success('Password changed successfully!');
          navigate('/auth');
        } else {
          const err = await resp.json();
          toast.error(err?.message || 'Failed to change password');
        }
      } else {
        // Forgot flow: step 1 if no otp/token provided yet, request reset; else perform reset
        if (!otpOrToken) {
          const r = await requestPasswordReset(username);
          unwrapData<any>(r, { successToast: true, errorToast: true });
          setOtpRequested(true);
          setCooldown(60); // 1 minute cooldown
        } else {
          const r = await resetPassword({ username, password: newPassword, token: otpOrToken.trim() });
          unwrapData<any>(r, { successToast: true, errorToast: true });
          navigate('/auth');
        }
      }
    } catch (error) {
      console.error('Password change/reset error:', error);
      toast.error('An error occurred while updating password');
    } finally {
      setLoading(false);
    }
  };

  // Cooldown timer for resend OTP
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <nav className="border-b border-border/40 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button 
            onClick={() => navigate('/auth')} 
            className="flex items-center space-x-2 hover:text-primary transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm text-muted-foreground">Back to Login</span>
          </button>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              BillPortal
            </span>
          </div>
        </div>
      </nav>

      {/* Change Password Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Card className="shadow-card border-border/50">
            <CardHeader className="text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="text-2xl font-bold">{mode === 'change' ? 'Change Password' : 'Forgot Password'}</CardTitle>
              <CardDescription>
                {mode === 'change' ? 'Update your account password securely' : 'Enter your username to receive a 6-digit OTP and set a new password (OTP valid for 5 minutes).'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                 {/* Username */}
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="Enter your username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>

                {/* Current Password (change mode only) */}
                {mode === 'change' && (
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <div className="relative">
                    <Input
                      id="current-password"
                      type={showCurrentPassword ? "text" : "password"}
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    >
                      {showCurrentPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>
                )}

                {/* New Password */}
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm New Password */}
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>

                {/* OTP for forgot reset */}
                {mode === 'forgot' && (
                  <div className="space-y-2">
                    <Label htmlFor="otp-token">OTP Code</Label>
                    <Input
                      id="otp-token"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      placeholder="Enter 6-digit OTP"
                      value={otpOrToken}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpOrToken(digits);
                      }}
                    />
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">OTP valid for 5 minutes.</p>
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:text-blue-700 disabled:text-muted-foreground"
                        onClick={async () => {
                          if (cooldown > 0) return;
                          try {
                            const r = await requestPasswordReset(username);
                            unwrapData<any>(r, { successToast: true, errorToast: true });
                            setOtpRequested(true);
                            setCooldown(60);
                          } catch (err) {}
                        }}
                        disabled={!username || cooldown > 0}
                        aria-disabled={!username || cooldown > 0}
                      >
                        {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Password Requirements */}
                <div className="bg-muted/50 p-3 rounded-lg space-y-2">
                  <h4 className="text-sm font-medium">Password Requirements:</h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      At least 8 characters long
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      Different from current password
                    </li>
                  </ul>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {mode === 'change' ? 'Change Password' : (otpOrToken ? 'Set New Password' : (otpRequested ? 'Send Reset Again' : 'Send Reset'))}
                </Button>
              </form>

              {/* Back to Login */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/auth')}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Back to Login
                </button>
              </div>

              {/* Mode Toggle */}
              <div className="text-center mt-2">
                <button
                  type="button"
                  onClick={() => setMode(m => (m === 'change' ? 'forgot' : 'change'))}
                  className="text-xs text-muted-foreground hover:text-primary"
                >
                  {mode === 'change' ? 'Use Forgot Password instead' : 'Use Change Password (I know current password)'}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
